import React, { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Form, Input, Checkbox, message } from 'antd'
import { useHistory, Redirect } from 'react-router-dom'
import { jwtDecode } from 'jwt-decode'
import { Mail, Lock, Eye, EyeOff, BarChart3, Shield, ArrowRight, MapPin } from 'lucide-react'
import { requiredMessage } from '../../utils/helpers'
import { authToken, loginUser, setOrganization } from '../../Redux/actions/authActions'
import { AUTH } from '../../utils/apis'
import { allPaths, version } from '../../utils/constants'
import { elmecLogo } from '../../assets'
import { secureApi } from '../../Config/api'
import './LoginPage.css'

const Login = () => {
  const user = useSelector(state => state?.authReducer?.user)
  const dispatch = useDispatch()
  const history = useHistory()

  const [passwordVisible, setPasswordVisible] = useState(false)
  const [rememberMe, setRememberMe] = useState(false)
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    const initGoogle = () => {
      if (window.google?.accounts?.id) {
        window.google.accounts.id.initialize({
          client_id: '989212354221-0jmeublh1rl9k1mi4us40tsu8tva7g1e.apps.googleusercontent.com',
          callback: handleGoogleLogin,
          auto_select: false
        })

        window.google.accounts.id.renderButton(
          document.getElementById('signInDiv'),
          {
            theme: 'outline',
            size: 'large'
          }
        )
      }
    }

    if (!window.google?.accounts?.id) {
      const script = document.createElement('script')
      script.src = 'https://accounts.google.com/gsi/client'
      script.async = true
      script.defer = true
      script.onload = initGoogle
      script.onerror = () => message.error('Failed to load Google script.')
      document.body.appendChild(script)
    } else {
      initGoogle()
    }
  }, [])

  const onFinish = async values => {
    try {
      setLoading(true)
      const response = await secureApi.post(AUTH?.LOGIN, {
        email: values?.email,
        password: values?.password,
        remember: rememberMe
      })

      if (response?.success) {
        dispatch(loginUser(response?.user))
        dispatch(setOrganization(response?.organization))
        dispatch(authToken(response?.encodedToken))
        history.push(allPaths?.HOME)
        message.success(response.message)
      } else {
        message.error(response.message)
      }
    } catch (error) {
      const errMsg = error?.response?.data?.message || 'Login failed. Please try again.'
      message.error(errMsg)
    } finally {
      setLoading(false)
    }
  }

  const handleGoogleLogin = async response => {
    try {
      if (!response.credential) {
        message.error('Google login failed. No credential received.')
        return
      }

      const data = jwtDecode(response.credential)
      // The checkbox applies to Google sign-in too.
      const apiResponse = await secureApi.post(AUTH?.GOOGLELOGIN, { email: data?.email, remember: rememberMe })

      if (apiResponse?.success) {
        dispatch(setOrganization(apiResponse?.organization))
        dispatch(loginUser(apiResponse?.user))
        dispatch(authToken(apiResponse?.encodedToken))
        history.push(allPaths.HOME)
        message.success('Login with Google successful!')
      } else {
        message.error(apiResponse?.message)
      }
    } catch {
      message.error('Google login failed. Please try again.')
    }
  }

  const handleRememberMeChange = e => {
    setRememberMe(e.target.checked)
  }

  const forgot = () => {
    history.push(allPaths.FORGOT_PASSWORD)
  }

  const togglePasswordVisibility = () => {
    setPasswordVisible(!passwordVisible)
  }

  if (user) return <Redirect to={allPaths.HOME} />

  return (
    <div className="login-container">
      <div className="left-section">
        <div className="left-content">
          <div className="logo-section">
            <img src={elmecLogo} alt="Elmec Elettronica" className="login-logo" />
            <span className="login-product-badge">Mobility Monitor</span>
          </div>

          <div className="welcome-section">
            <h1 className="main-heading">
              Welcome to your <br />
              <span className="dashboard-text">Dashboard</span>
            </h1>
            <p className="description">
              Manage your vehicles, devices, and operations with<br />
              our comprehensive platform designed for efficiency<br />
              and control.
            </p>
          </div>

          <div className="features-section">
            <div className="feature-card">
              <div className="feature-icon analytics-icon">
                <BarChart3 size={24} />
              </div>
              <div className="feature-content">
                <h3 className="feature-title">Analytics</h3>
                <p className="feature-description">Real-time insights and<br />reporting</p>
              </div>
            </div>

            <div className="feature-card">
              <div className="feature-icon security-icon">
                <Shield size={24} />
              </div>
              <div className="feature-content">
                <h3 className="feature-title">Security</h3>
                <p className="feature-description">Enterprise-grade protection</p>
              </div>
            </div>

            <div className="feature-card">
              <div className="feature-icon tracking-icon">
                <MapPin size={24} />
              </div>
              <div className="feature-content">
                <h3 className="feature-title">Live tracking</h3>
                <p className="feature-description">Fleet positions and routes in real time</p>
              </div>
            </div>
          </div>

          <div className="login-footer">
            <span>&copy; {new Date().getFullYear()} Elmec Elettronica</span>
            <span className="login-footer-dot" />
            <span>v{version}</span>
            <span className="login-footer-dot" />
            <span className="login-footer-link" onClick={() => history.push(allPaths?.PRIVACY_POLICY)}>Privacy policy</span>
          </div>
        </div>
        <div className="login-blob login-blob-1" />
        <div className="login-blob login-blob-2" />
      </div>
      <div className="right-section">
        <div className="login-form-container">
          <div className="form-header">
            <h2 className="form-title">Welcome back</h2>
            <p className="form-subtitle">
              Sign in to access your dashboard and manage your<br />
              operations
            </p>
          </div>

          <Form name="form" onFinish={onFinish} layout="vertical" requiredMark={false} className="login-form">
            <Form.Item
              name="email"
              label="Email address"
              rules={[{ required: true, message: requiredMessage('Email') }]}
              className="input-group"
            >
              <div className="input-wrapper">
                <Mail className="input-icon" size={20} />
                <Input
                  name="email"
                  placeholder="example@example.com"
                  className="form-input"
                />
              </div>
            </Form.Item>

            <Form.Item
              name="password"
              label="Password"
              rules={[
                { required: true, message: requiredMessage('Password') },
                { min: 8, message: 'Password must be at least 8 characters' }
              ]}
              className="input-group"
            >
              <div className="input-wrapper">
                <Lock className="input-icon" size={20} />
                <Input
                  type={passwordVisible ? 'text' : 'password'}
                  name="password"
                  placeholder="••••••••"
                  className="form-input"
                />
                <button
                  type="button"
                  onClick={togglePasswordVisibility}
                  className="password-toggle"
                >
                  {passwordVisible ? <EyeOff size={20} /> : <Eye size={20} />}
                </button>
              </div>
            </Form.Item>
            <div className="remember-me">
              {/* <Form.Item> */}
                <Checkbox checked={rememberMe} onChange={handleRememberMeChange}>
                  <span className="welcome-login-text">Remember for 30 days</span>
                </Checkbox>
              {/* </Form.Item> */}
              <p className="forgot-password-text" onClick={forgot}>
                Forgot password
              </p>
            </div>
            <Form.Item>
              <button
                type="submit"
                disabled={loading}
                className={`submit-button ${loading ? 'loading' : ''}`}
              >
                {loading ? 'Signing in...' : (
                  <>
                    Sign in
                    <ArrowRight size={20} />
                  </>
                )}
              </button>
            </Form.Item>
          </Form>
        </div>
      </div>
    </div>
  )
}

export default Login